import {
  type PBIExportableReport,
  type PBIReport,
  type PBIReportWithoutProfile,
  type PBIDemoReport,
  type PBIDemoReportWithToken,
  type PBIToken,
} from 'managed-analytics/domain/PBIReport'

import { GetTokenParams } from './PBIRepository'

export type RawPBIReport = {
  uuid: string
  dataset_id: string
  group_id: string
  report_id: string
  report_name: string
  rls_filter: string
  customer_type: string
  role_name: string
  profile: string
}

export type RawPBIReportWithoutProfile = Omit<
  RawPBIReport,
  'rls_filter' | 'customer_type' | 'role_name' | 'profile'
>

export type RawPBIDemoReport = {
  uuid: string
  dataset_id: string
  group_id: string
  report_id: string
  report_name: string
  description: string
  thumbnail_url: string
  demo_without_data: boolean
}

export type RawPBIDemoReportWithToken = RawPBIDemoReport & {
  embed_token: string | null
  embed_token_error: string | null
  embed_token_expires_in: string | null
}

export type RawPBIExportableReport = {
  report_name: string
  group_id: string
  report_id: string
  dataset_id: string
  role_name: string
  pdf_demo_file: string
  role_uuid?: string
  user_uuid?: string
  rls_filter: string
  is_execution_blocked?: boolean
}

export type RawPBIToken = {
  expires_in: string
  embed_token: string
}

export function mapApiToPBIReports(reports: Array<RawPBIReport>): Array<PBIReport> {
  return reports.map(report => ({
    uuid: report.uuid,
    datasetId: report.dataset_id,
    groupId: report.group_id,
    reportId: report.report_id,
    reportName: report.report_name,
    rlsFilter: report.rls_filter,
    customerType: report.customer_type,
    roleName: report.role_name,
    profile: report.profile,
  }))
}

export function mapApiToPBIReport(report: RawPBIReportWithoutProfile): PBIReportWithoutProfile {
  return {
    uuid: report.uuid,
    datasetId: report.dataset_id,
    groupId: report.group_id,
    reportId: report.report_id,
    reportName: report.report_name,
  }
}

export function mapApiToPBIDemoReports(reports: Array<RawPBIDemoReport>): Array<PBIDemoReport> {
  return reports.map(report => ({
    uuid: report.uuid,
    datasetId: report.dataset_id,
    groupId: report.group_id,
    reportId: report.report_id,
    reportName: report.report_name,
    description: report.description,
    thumbnailUrl: report.thumbnail_url,
    demoWithoutData: report.demo_without_data,
  }))
}

export function mapApiToPBIDemoReport(report: RawPBIDemoReportWithToken): PBIDemoReportWithToken {
  return {
    uuid: report.uuid,
    datasetId: report.dataset_id,
    groupId: report.group_id,
    reportId: report.report_id,
    reportName: report.report_name,
    description: report.description,
    thumbnailUrl: report.thumbnail_url,
    demoWithoutData: report.demo_without_data,
    embedToken: report.embed_token,
    embedTokenError: report.embed_token_error,
    embedTokenExpiresIn: report.embed_token_expires_in,
  }
}

export function mapApiToPBIExportableReports(
  reports: Array<RawPBIExportableReport>
): Array<PBIExportableReport> {
  return reports.map(report => ({
    reportName: report.report_name,
    groupId: report.group_id,
    reportId: report.report_id,
    datasetId: report.dataset_id,
    roleName: report.role_name,
    userUuid: report.user_uuid,
    roleUuid: report.role_uuid,
    rlsFilter: report.rls_filter,
    pdfDemoFile: report.pdf_demo_file,
    pdfDemoFileExpiresAt: report.pdf_demo_file
      ? extractExpirationDateFromUrl(report.pdf_demo_file)
      : null,
    is_execution_blocked: report.is_execution_blocked,
  }))
}

export function mapPBIReportToApi(report: PBIReport): GetTokenParams {
  return {
    dataset_id: report.datasetId,
    group_id: report.groupId,
    report_id: report.reportId,
    rls_filter: report.rlsFilter,
    customer_type: report.customerType,
    role_name: report.roleName,
  }
}

export function mapApiToPBIToken({ embed_token, expires_in }: RawPBIToken): PBIToken {
  return {
    embedToken: embed_token,
    expiresIn: expires_in,
  }
}

function extractExpirationDateFromUrl(url: string): Date | null {
  try {
    const parsed = new URL(url)
    const expires = parsed.searchParams.get('Expires')
    if (!expires) return null

    const timestamp = parseInt(expires, 10)
    return new Date(timestamp * 1000)
  } catch {
    return null
  }
}
